import { useEffect, useState } from "react";
import useSWR from "swr";
import { Drawer, Loader, Stack, Tabs, Text } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { fetcher } from "../../../../services/fetcher";
import { getTravellerDetailEndpoint } from "../../utils/travellerDrawerConstants";
import { getTravellerName } from "../../utils/allPolicyHelpers";
import TravellerDrawerHeader from "./TravellerDrawerHeader";
import TravellerDrawerProfile from "./TravellerDrawerProfile";
import TravellerDrawerCoverage from "./TravellerDrawerCoverage";
import TravellerDrawerDocuments from "./TravellerDrawerDocuments";
import TravellerDrawerEmergency from "./TravellerDrawerEmergency";
import TravellerDrawerMedicalHistory from "./TravellerDrawerMedicalHistory";
import classes from "./TravellerPolicyDrawer.module.css";

const TravellerPolicyDrawer = ({ opened, travellerId, onClose }) => {
  const isMobile = useMediaQuery("(max-width: 48em)");
  const [tab, setTab] = useState("profile");
  const endpoint = opened && travellerId ? getTravellerDetailEndpoint(travellerId) : null;

  const { data, isLoading, error } = useSWR(endpoint, fetcher, {
    revalidateOnFocus: false,
    shouldRetryOnError: false,
  });

  useEffect(() => {
    if (opened) setTab("profile");
  }, [opened, travellerId]);

  const traveller = data;

  return (
    <Drawer
      opened={opened}
      onClose={onClose}
      position="right"
      size={isMobile ? "100%" : "45%"}
      zIndex={300}
      withCloseButton
      classNames={{ body: classes.drawerBody, content: classes.drawerContent }}
      title={
        traveller ? (
          <Stack gap={4}>
            <Text size="xs" c="dimmed">TRAVELLER #{traveller.id}</Text>
            <Text fw={700} size="lg" lineClamp={2}>{getTravellerName(traveller)}</Text>
          </Stack>
        ) : (
          "Traveller Details"
        )
      }
    >
      {isLoading ? (
        <Stack align="center" py="xl"><Loader size="sm" /></Stack>
      ) : error ? (
        <Text size="sm" c="red">Failed to load traveller details.</Text>
      ) : (
        <Stack gap="md">
          <TravellerDrawerHeader traveller={traveller} />
          <Tabs value={tab} onChange={setTab}>
            <Tabs.List className={classes.tabList}>
              <Tabs.Tab value="profile">Profile</Tabs.Tab>
              <Tabs.Tab value="coverage">Coverage</Tabs.Tab>
              <Tabs.Tab value="documents">Documents</Tabs.Tab>
              <Tabs.Tab value="medical">Medical History</Tabs.Tab>
              <Tabs.Tab value="emergency">Emergency</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="profile" pt="md">
              <TravellerDrawerProfile traveller={traveller} />
            </Tabs.Panel>
            <Tabs.Panel value="coverage" pt="md">
              <TravellerDrawerCoverage traveller={traveller} />
            </Tabs.Panel>
            <Tabs.Panel value="documents" pt="md">
              <TravellerDrawerDocuments traveller={traveller} />
            </Tabs.Panel>
            <Tabs.Panel value="medical" pt="md">
              <TravellerDrawerMedicalHistory traveller={traveller} />
            </Tabs.Panel>
            <Tabs.Panel value="emergency" pt="md">
              <TravellerDrawerEmergency traveller={traveller} />
            </Tabs.Panel>
          </Tabs>
        </Stack>
      )}
    </Drawer>
  );
};

export default TravellerPolicyDrawer;
